import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a Canadian invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A standard Canadian invoice must include: your business name and address; the client name and address; invoice number and date; a description of goods or services; the amount charged; and payment terms. If you are registered for GST/HST, you must also include your Business Number (BN) with the RT0001 suffix, the GST/HST rate applied, the GST/HST amount, and the total including tax.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the GST/HST rate in Canada?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Canada has a federal GST rate of 5%. Some provinces combine this with a provincial tax into HST: Ontario 13%, New Brunswick 15%, Nova Scotia 15%, Prince Edward Island 15%, Newfoundland and Labrador 15%. British Columbia, Saskatchewan, and Manitoba have separate PST in addition to federal GST. Quebec has its own QST at 9.975% on top of federal GST.',
      },
    },
    {
      '@type': 'Question',
      name: 'When do I need to register for GST/HST in Canada?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You must register for GST/HST if your total worldwide taxable supplies exceed $30,000 CAD in a single calendar quarter or in four consecutive calendar quarters. Below this threshold, registration is voluntary. Once registered, you must collect and remit GST/HST and include your Business Number on all invoices.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a free Canadian invoice online?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is a free invoice generator for Canadian freelancers and businesses. Create GST/HST invoices or standard invoices in CAD or USD. Download a professional PDF. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
    {
      '@type': 'Question',
      name: 'What currency should Canadian invoices use?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For Canadian clients, invoice in CAD (Canadian Dollar). For US or international clients, you may invoice in USD or the client\'s preferred currency. Invilic supports CAD, USD, and any other currency you need.',
      },
    },
  ],
};

export default function InvoiceGeneratorCanadaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Canada: Free GST/HST Invoice Maker | Invilic"
        description="Free invoice generator for Canadian freelancers and businesses. Create GST/HST invoices or standard invoices in CAD. Download professional PDF free, no sign-up needed."
        canonical="https://www.invilic.com/invoice-generator-canada"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇨🇦 Canada
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Canada
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Canadian businesses and freelancers. Create GST/HST invoices with your Business Number, or standard invoices if you are below the registration threshold. Download professional PDF invoices in CAD instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/create/vat"
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200"
            >
              Create GST/HST Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/create/freelancer"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200"
            >
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Invoice types */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Which Invoice Do You Need?
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: Percent,
                title: 'GST/HST Invoice',
                subtitle: 'For GST/HST-registered businesses',
                features: ['Business Number (BN) field', 'GST (5%) or HST (13–15%)', 'Auto tax calculation', 'Net + tax + total breakdown', 'CAD currency'],
                to: '/create/vat',
                color: 'red',
                btnClass: 'bg-red-600 hover:bg-red-700',
              },
              {
                icon: FileText,
                title: 'Standard Invoice',
                subtitle: 'For businesses under $30,000 threshold',
                features: ['No GST/HST required', 'CAD or USD billing', 'Custom line items', 'Logo upload', 'Payment terms'],
                to: '/create/freelancer',
                color: 'blue',
                btnClass: 'bg-blue-600 hover:bg-blue-700',
              },
              {
                icon: Globe,
                title: 'International Invoice',
                subtitle: 'For billing US or overseas clients',
                features: ['USD, EUR, GBP billing', 'Wire transfer details', 'Multi-currency support', 'Export invoice format'],
                to: '/create/international',
                color: 'indigo',
                btnClass: 'bg-indigo-600 hover:bg-indigo-700',
              },
            ].map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={to}
                  className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}
                >
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* GST/HST guide */}
        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
            Canadian Tax Rates by Province
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { province: 'Ontario', rate: 'HST 13%' },
              { province: 'British Columbia', rate: 'GST 5% + PST 7%' },
              { province: 'Quebec', rate: 'GST 5% + QST 9.975%' },
              { province: 'Alberta', rate: 'GST 5% only' },
              { province: 'Nova Scotia', rate: 'HST 15%' },
              { province: 'Manitoba', rate: 'GST 5% + PST 7%' },
              { province: 'New Brunswick', rate: 'HST 15%' },
              { province: 'Saskatchewan', rate: 'GST 5% + PST 6%' },
            ].map(({ province, rate }) => (
              <div key={province} className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{province}</span>
                <span className="text-sm text-red-600 dark:text-red-400 font-semibold">{rate}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">Enter the applicable rate in the tax field when creating your invoice.</p>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Canadian Invoice Generator: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Canadian Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for Canadian businesses in under 2 minutes. No account required.</p>
          <Link
            to="/create/vat"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200 text-lg"
          >
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
